<script lang="ts" setup>
import { micromark } from 'micromark'

const { t, locale } = useI18n()
const shippingGuaranteeModalOpen = ref(false)

const modals = [
  {
    id: 'shipping',
    icon: 'i-heroicons:truck',
  },
  {
    id: 'return',
    icon: 'i-heroicons:arrow-path-rounded-square-20-solid',
  },
  {
    id: 'payment',
    icon: 'i-heroicons:arrow-path-rounded-square-20-solid',
  },
  {
    id: 'customer-protection',
    icon: 'i-heroicons:user',
  },
  {
    id: 'satisfaction-guarantee',
    icon: 'i-heroicons:shield-check-20-solid',
  },
  {
    id: 'best-merchants',
    icon: 'i-heroicons:user-group-16-solid',
  },
]
const openModal = ref<string | null>(null)

const paymentProviders = [
  { name: 'payment:blik-full', class: '', locale: 'pl' },
  { name: 'payment:p24', class: '', locale: 'pl' },
  { name: 'payment:visa', class: 'text-visa dark:text-white', locale: '' },
  { name: 'payment:mastercard', class: '', locale: '' },
  { name: 'payment:apple-pay', class: '', locale: '' },
  { name: 'payment:google-pay', class: '', locale: '' },
  { name: 'payment:maestro', class: '', locale: '' },
  { name: 'payment:amex-square', class: 'text-amex', locale: '' },
  { name: 'payment:dinersclub-square', class: '', locale: 'cs' },
  { name: 'payment:discover-square', class: '', locale: 'cs' },
]

const icons = computed(() => paymentProviders.filter(payment => !payment.locale || payment.locale === locale.value))

const { data: value } = await useAsyncData('policies', async () => {
  if (openModal.value)
    return await queryCollection('policies').path(`/${locale.value}/policies/${openModal.value}`).first()
  return null
}, {
  watch: [openModal],
})

function closeModal() {
  openModal.value = null
}
</script>

<template>
  <div v-for="modal in modals" :key="modal.id">
    <NModal
      :open="openModal === modal.id"
      @close="closeModal"
    >
      <HDialogPanel
        class="max-w-md w-full transform overflow-hidden rounded-2xl bg-white p6 text-left align-middle shadow-xl transition-all"
      >
        <div class="absolute right-0 top-0 pr4 pt4">
          <NButton
            :id="`close-${modal.id}`"
            n="lg borderless"
            icon="i-heroicons:x-mark"
            class="p-0.25em text-context children:m-0"
            @click="closeModal"
          />
        </div>

        <div>
          <div class="mx-auto h12 w12 flex items-center justify-center rounded-full bg-orange-50 dark:bg-orange-900">
            <Icon :name="modal.icon" class="text-2xl text-orange5" />
          </div>

          <div class="mt3 text-center md:mt5">
            <div class="mb4 mt2">
              <div class="prose-sm text-left prose">
                <ContentRenderer v-if="value" :value="value" />
              </div>
            </div>

            <div>
              <UiButton
                :id="`${modal.id}-got-it`"
                variant="orange"
                class="w-full justify-center rounded-full fw600 leading-8"
                @click="closeModal"
              >
                {{ t('general.got_it.label') }}
              </UiButton>
            </div>
          </div>
        </div>
      </HDialogPanel>
    </NModal>
  </div>
  <div>
    <NModal :open="shippingGuaranteeModalOpen" @close="shippingGuaranteeModalOpen = false">
      <HDialogPanel
        class="max-w-md w-full transform overflow-hidden rounded-2xl bg-white p6 text-left align-middle shadow-xl transition-all"
      >
        <div class="absolute right-0 top-0 pr4 pt4">
          <NButton
            id="close-shipping-guarantee"
            n="lg borderless"
            icon="i-heroicons:x-mark"
            class="p-0.25em text-context children:m-0"
            @click="shippingGuaranteeModalOpen = false"
          />
        </div>

        <div>
          <div n="orange5" class="mx-auto h12 w12 flex items-center justify-center rounded-full bg-orange-50 dark:bg-orange-900">
            <Icon name="i-mdi:shield-home" n="2xl" class="text-context" />
          </div>

          <div class="mt3 text-center sm:mt5">
            <h3 id="modal-title" class="text-base c-slate9 font-semibold leading-6">
              {{ t('product.shipping_guarantee.label') }}
            </h3>

            <div class="mb4 mt2">
              <div n="sm slate5 dark:slate7" class="text-left text-context prose" v-html="micromark(t('product.shipping_guarantee.body'))" />
            </div>

            <div>
              <NButton
                id="got-it"
                n="orange5 solid"
                class="w-full justify-center rounded-full fw600 leading-8"
                @click="shippingGuaranteeModalOpen = false"
              >
                {{ t('general.got_it.label') }}
              </NButton>
            </div>
          </div>
        </div>
      </HDialogPanel>
    </NModal>
  </div>

  <div class="hidden md:block">
    <div class="r-md !mt5">
      <div class="block block border-1 b-gray-300 !rounded-sm">
        <div class="flex items-center justify-between border-b b-gray-300">
          <div class="w-full px3 py4">
            <div class="flex items-center gap-2 rounded-sm">
              <Icon name="i-heroicons:truck" class="text-xl text-success -mt0.5" />
              <div class="w-full flex items-center justify-between">
                <span class="text-sm">{{ t('policies.free_shipping') }}</span>
                <NTip
                  n="xs emerald5"
                  icon="i-ri:flashlight-fill"
                  class="px2 py1 text-context fw600"
                >
                  {{ t('checkout.shipping.fast_delivery_available') }}
                </NTip>
              </div>
            </div>
          </div>
        </div>
        <div class="flex items-center justify-between overflow-hidden border-b b-gray-300">
          <div class="w-full px3 py4">
            <div class="flex items-center gap-2 rounded-sm">
              <Icon name="i-heroicons:lock-closed" class="shrink-0 text-xl text-success -mt0.5" />
              <div class="w-full flex items-center justify-between">
                <span class="text-sm">
                  {{ t('payment_acceptance.secure_payment') }}
                </span>
                <div class="ml2 flex items-center overflow-x-auto no-scrollbar md:ml0">
                  <Icon v-for="icon in icons" :key="icon.name" v-bind="icon" class="text-md mr4" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex items-center justify-between border-b b-gray-300">
          <div class="w-full px3 py4">
            <div class="flex items-center gap-2 rounded-sm">
              <Icon name="i-heroicons:gift" class="text-xl text-success -mt0.5" />
              <div class="w-full flex items-center justify-between">
                <span class="text-sm">{{ t('policies.gift_with_every_purchase') }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-between overflow-hidden border-b b-gray-300">
          <div class="w-full px3 py4">
            <div class="flex items-center gap-2 rounded-sm">
              <Icon name="i-heroicons:shield-check" class="text-xl text-success -mt0.5" />
              <div class="w-full flex items-center justify-between">
                <span class="text-sm">{{ t('policies.customer_satisfaction_guarantee') }}</span>
                <NTip
                  id="shipping-guarantee"
                  n="xs emerald5"
                  icon="i-mdi:shield-home"
                  class="px2 py1 text-context fw600 hover:cursor-pointer"
                  @click.stop="shippingGuaranteeModalOpen = true"
                >
                  {{ t('product.shipping_guarantee.label') }}
                </NTip>
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-between b-gray-300">
          <div class="w-full px3 py4">
            <div class="flex items-center gap-2 rounded-sm">
              <Icon name="lucide:badge-check" class="text-xl text-success -mt0.5" />
              <div class="w-full flex items-center justify-between">
                <span class="text-sm">
                  {{ t('product.two_years_warranty_on_everything') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="block md:hidden">
    <div class="mb4 block bg-slate/10 pb1 shadow-inner -mx4 md:hidden" />

    <div class="">
      <div class="w-full flex flex-col">
        <div
          class="flex items-center gap-2 rounded-sm"
          @click.stop="openModal = 'shipping'"
        >
          <div class="w-full flex flex-col py2 -mt-2 space-y-1">
            <div class="flex items-center gap-x-1 -mt2">
              <Icon name="i-heroicons:truck" class="text-xl text-success -mt0.5" />
              <span class="text-sm text-success fw600">
                {{ t('product.policies.shipping.label') }}
              </span>
            </div>
            <span class="text-xs">{{ t('product.policies.shipping.info') }}</span>
          </div>
        </div>
      </div>

      <div class="block bg-slate/10 pb1 shadow-inner -mx4 md:hidden" />

      <div class="w-full flex flex-col">
        <div
          class="flex items-center gap-2 rounded-sm"
          @click="openModal = 'return'"
        >
          <div class="w-full flex flex-col py2 space-y-1">
            <div class="flex items-center gap-x-1">
              <Icon name="i-heroicons:arrow-path-rounded-square-20-solid" class="text-xl text-success -mt0.5" /> <span class="text-sm text-success fw600">{{ t('product.policies.claims.label') }}</span>
            </div>
            <span class="text-xs">{{ t('product.policies.claims.info') }}</span>
          </div>
        </div>
      </div>

      <div class="block bg-slate/10 pb1 shadow-inner -mx4 md:hidden" />

      <div class="w-full flex flex-col">
        <div
          class="flex items-center gap-2 rounded-sm"
          @click="openModal = 'payment'"
        >
          <div class="flex items-center gap-x-1 py2">
            <Icon name="i-heroicons:lock-closed" class="text-xl text-success -mt0.5" />
            <span class="text-sm text-success fw600">
              {{ t('payment_acceptance.secure_payment') }}
            </span>
          </div>
        </div>
        <div class="flex gap-x-2 overflow-x-auto pr4 no-scrollbar -mr4">
          <div class="ml2 flex items-center overflow-x-auto no-scrollbar md:ml0">
            <Icon v-for="icon in icons" :key="icon.name" v-bind="icon" class="text-md mr4" />
          </div>
        </div>
      </div>
      <div class="mt2 block bg-slate/10 pb1 shadow-inner -mx4 md:hidden" />
      <div class="w-full flex flex-col pb2 md:pb0">
        <div class="flex items-center gap-2 rounded-sm">
          <div class="flex items-center gap-x-1 py2">
            <Icon name="i-heroicons:shield-check-16-solid" class="text-xl text-success -mt0.5" />
            <span class="text-sm text-success fw600">
              {{ t('product.policies.shop_with_us') }}
            </span>
          </div>
        </div>
        <div class="flex gap-x-2 overflow-x-auto pr4 no-scrollbar -mr4">
          <div
            class="w-fit rd-sm bg-slate-1 px2 py1"
            @click="openModal = 'customer-protection'"
          >
            <span class="flex gap-x-1 ws-nowrap text-sm text-success fw600">
              {{ t('product.policies.privacy.label') }}
            </span>
            <p class="flex gap-x-1 ws-nowrap text-xs c-slate5 dark:c-slate7">
              <Icon name="i-heroicons:check-circle" class="text-xs text-success" />
              {{ t('product.policies.privacy.secure_checkout') }}
            </p>
            <p class="flex gap-x-1 ws-nowrap text-xs c-slate5 dark:c-slate7">
              <Icon name="i-heroicons:check-circle" class="text-xs text-success" />
              {{ t('product.policies.privacy.tls') }}
            </p>
            <p class="flex gap-x-1 ws-nowrap text-xs c-slate5 dark:c-slate7">
              <Icon name="i-heroicons:check-circle" class="text-xs text-success" />
              {{ t('product.policies.privacy.delivery_guarantee') }}
            </p>
          </div>
          <div
            class="w-fit rd-sm bg-slate-1 px2 py1"
            @click="openModal = 'satisfaction-guarantee'"
          >
            <span class="flex gap-x-1 ws-nowrap text-sm text-success fw600">
              {{ t('product.policies.satisfaction.label') }}
            </span>
            <p class="flex gap-x-1 ws-nowrap text-xs c-slate5 dark:c-slate7">
              <Icon name="i-heroicons:check-circle" class="text-xs text-success" />
              {{ t('product.policies.satisfaction.gift') }}
            </p>
            <p class="flex gap-x-1 ws-nowrap text-xs c-slate5 dark:c-slate7">
              <Icon name="i-heroicons:check-circle" class="text-xs text-success" />
              {{ t('product.policies.satisfaction.customer_care') }}
            </p>
            <p class="flex gap-x-1 ws-nowrap text-xs c-slate5 dark:c-slate7">
              <Icon name="i-heroicons:check-circle" class="text-xs text-success" />
              {{ t('product.policies.satisfaction.change_and_return') }}
            </p>
          </div>

          <div
            class="w-fit rd-sm bg-slate-1 px2 py1"
            @click="openModal = 'best-merchants'"
          >
            <span class="flex gap-x-1 ws-nowrap text-sm text-success fw600">
              {{ t('product.policies.supplier.label') }}
            </span>
            <p class="flex gap-x-1 ws-nowrap text-xs c-slate5 dark:c-slate7">
              <Icon name="i-heroicons:check-circle" class="text-xs text-success" />
              {{ t('product.policies.supplier.quality') }}
            </p>
            <p class="flex gap-x-1 ws-nowrap text-xs c-slate5 dark:c-slate7">
              <Icon name="i-heroicons:check-circle" class="text-xs text-success" />
              {{ t('product.policies.supplier.fast_ship_out') }}
            </p>
            <p class="flex gap-x-1 ws-nowrap text-xs c-slate5 dark:c-slate7">
              <Icon name="i-heroicons:check-circle" class="text-xs text-success" />
              {{ t('product.policies.supplier.years_of_experience') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
